import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  Divider,
  Switch,
  FormControlLabel,
  Grid,
  Chip,
  Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import LaunchIcon from '@mui/icons-material/Launch';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSelector, useDispatch } from 'react-redux';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import BoardRouter from './BoardRouter';

import { BOARD_TYPE_TITLES } from './CreateBoardModal';

/**
 * Modal para ver y editar detalles de un tablero existente
 * Sigue el principio Open/Closed (OCP) de SOLID
 */
const ViewBoardModal = ({ 
  open, 
  onClose, 
  board, 
  onUpdate, 
  onOpenBoard
}) => {
  const dispatch = useDispatch();
  const agent = useSelector(state => state.agent);
  const navigate = useNavigate();
  
  // Estados para el modo de visualización/edición
  const [isEditMode, setIsEditMode] = useState(false);
  
  // Estados para el formulario básico
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isPinned, setIsPinned] = useState(false);
  
  // Estado para manejar errores de validación
  const [errors, setErrors] = useState({});
  
  // Estado para manejar metadatos adicionales
  const [metaDataFields, setMetaDataFields] = useState([]);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');
  const [showAddMetaField, setShowAddMetaField] = useState(false);
  
  // Formatear la fecha de última actualización si existe
  const formattedUpdateDate = board?.updated_at 
    ? format(parseISO(board.updated_at), "dd MMMM yyyy 'a las' HH:mm", { locale: es })
    : 'Sin actualizar';
  
  const formattedCreateDate = board?.created_at
    ? format(parseISO(board.created_at), "dd MMMM yyyy 'a las' HH:mm", { locale: es })
    : 'Fecha desconocida';
  
  // Cargar los datos del tablero cuando cambie el tablero seleccionado
  useEffect(() => {
    if (board) {
      setName(board.name || '');
      setIsPinned(board.pinned || false);
      
      // Cargar descripción desde la propiedad principal o desde metadatos
      let boardDescription = board.description || '';
      let metaDataItems = [];
      
      // Procesar metadatos
      if (board.meta_data) {
        try {
          const parsedMetaData = typeof board.meta_data === 'string'
            ? JSON.parse(board.meta_data)
            : board.meta_data;
          
          // Si hay descripción en metadatos y no hay en la propiedad principal
          if (parsedMetaData.descripcion && !boardDescription) {
            boardDescription = parsedMetaData.descripcion;
          }
          
          // Convertir otros metadatos al formato de campos
          Object.entries(parsedMetaData).forEach(([key, value]) => {
            if (key !== 'descripcion') {
              metaDataItems.push({ key, value });
            }
          });
        } catch (error) {
          console.error('Error al procesar metadatos del tablero:', error);
        }
      }
      
      setDescription(boardDescription);
      setMetaDataFields(metaDataItems);
    }
  }, [board]);
  
  // Restablecer modo de edición al cerrar
  useEffect(() => {
    if (!open) {
      setIsEditMode(false);
      setShowAddMetaField(false);
    }
  }, [open]);
  
  // Función para validar el formulario
  const validateForm = () => {
    const newErrors = {};
    
    if (!name.trim()) {
      newErrors.name = 'El nombre del tablero es obligatorio';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Función para añadir un nuevo campo de metadatos
  const handleAddMetaField = () => {
    if (newKey.trim() && newValue.trim()) {
      setMetaDataFields([
        ...metaDataFields,
        { key: newKey.trim(), value: newValue.trim() }
      ]);
      setNewKey('');
      setNewValue('');
      setShowAddMetaField(false);
    }
  };
  
  // Función para eliminar un campo de metadatos
  const handleRemoveMetaField = (index) => {
    const updatedFields = [...metaDataFields];
    updatedFields.splice(index, 1);
    setMetaDataFields(updatedFields);
  };
  
  // Función para preparar metadatos como JSON
  const buildMetaData = () => {
    // Crear un objeto base
    const metaData = {};
    
    // Añadir descripción solo si existe
    if (description && description.trim()) {
      metaData.descripcion = description.trim();
    }
    
    // Añadir campos personalizados
    metaDataFields.forEach(field => {
      metaData[field.key] = field.value;
    });
    
    // Retornar como string JSON
    return JSON.stringify(metaData);
  };
  
  /**
   * Capitaliza la primera letra de un texto y convierte el resto a minúsculas
   * @param {string} text - Texto a capitalizar
   * @returns {string} Texto con la primera letra en mayúscula y el resto en minúsculas
   */
  const capitalizeFirstLetter = (text) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  // Función para guardar los cambios
  const handleSaveChanges = () => {
    if (!validateForm()) return;
    
    // Preparar datos del tablero
    const boardData = {
      name: capitalizeFirstLetter(name.trim()), // Primera letra mayúscula, resto minúsculas
      pinned: isPinned,
      meta_data: buildMetaData(),
      id_agent: agent?.id || board.id_agent
    };
    
    // Llamar a la función de actualización
    onUpdate(board.id, boardData);
    
    // Salir del modo edición
    setIsEditMode(false);
  };
  
  // Función para obtener el título según el tipo de tablero
  const getBoardTypeTitle = () => {
    if (!board || !board.board_type) return 'Tablero';
    
    const boardType = board.board_type.toLowerCase().trim();
    
    // Buscar correspondencia en los tipos conocidos
    for (const [type, title] of Object.entries(BOARD_TYPE_TITLES)) {
      if (boardType === type || boardType.includes(type)) {
        return title;
      }
    }
    
    // Si no hay correspondencia, usar capitalizado
    return boardType.charAt(0).toUpperCase() + boardType.slice(1);
  };
  
  // Función para abrir el tablero
  const handleOpenBoard = () => {
    // Asegurarse de que el ID existe y convertirlo a string
    if (!board || !board.id) {
      console.error('No se puede abrir el tablero: ID no disponible', board);
      return;
    }
    
    const boardId = String(board.id);
    
    // Cerramos el modal primero
    onClose();
    
    // Pequeño delay para asegurar que el modal se ha cerrado
    setTimeout(() => {
      // Usar el router centralizado para la navegación limpia
      BoardRouter.navigateToBoard(boardId, navigate, dispatch);
    }, 100);
  };
  
  // Si no hay tablero seleccionado, no renderizar nada
  if (!board) return null;
  
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-board-details"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 600,
        maxWidth: '95%',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        p: 0,
        maxHeight: '95vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Encabezado del modal */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            backgroundColor: theme => theme.palette.primary.main,
            color: 'white'
          }}
        >
          <Typography id="modal-board-details" variant="subtitle1" sx={{ fontSize: '1.25rem', fontWeight: 'bold' }} component="div">
            {isEditMode ? 'Editar' : 'Detalles del'} {getBoardTypeTitle()}
          </Typography>
          <Box>
            <IconButton 
              color="inherit" 
              onClick={onClose}
              aria-label="cerrar"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        
        {/* Contenido principal */}
        <Box sx={{ p: 3, flex: 1, overflow: 'auto' }}>
          {isEditMode ? (
            /* Formulario de edición */
            <>
              {/* Nombre del tablero */}
              <TextField
                margin="normal"
                fullWidth
                label="Nombre del Tablero"
                value={name}
                onChange={(e) => setName(e.target.value)}
                error={!!errors.name}
                helperText={errors.name || ''}
                required
                autoFocus
              />
              
              {/* Descripción */}
              <TextField
                margin="normal"
                fullWidth
                label="Descripción"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                multiline
                rows={3}
              />
              
              {/* Opción de fijar */}
              <FormControlLabel
                control={
                  <Switch
                    checked={isPinned}
                    onChange={(e) => setIsPinned(e.target.checked)}
                    color="primary"
                  />
                }
                label="Fijar este tablero"
                sx={{ mt: 1, mb: 2 }}
              />
              
              {/* Metadatos adicionales */}
              <Box sx={{ mt: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="subtitle1">Información adicional</Typography>
                  <Button 
                    size="small" 
                    startIcon={<AddIcon />} 
                    onClick={() => setShowAddMetaField(true)}
                    variant="outlined"
                  >
                    Agregar campo
                  </Button>
                </Box>
                
                {/* Mostrar campos de metadatos existentes */}
                {metaDataFields.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {metaDataFields.map((field, index) => (
                      <Chip
                        key={index}
                        label={`${field.key}: ${field.value}`}
                        onDelete={() => handleRemoveMetaField(index)}
                        deleteIcon={<DeleteIcon />}
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                )}
                
                {/* Formulario para añadir nuevo campo de metadatos */}
                {showAddMetaField && (
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Agregar información adicional
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Clave"
                          value={newKey}
                          onChange={(e) => setNewKey(e.target.value)}
                          placeholder="Ej: estado"
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Valor"
                          value={newValue}
                          onChange={(e) => setNewValue(e.target.value)}
                          placeholder="Ej: activo"
                        />
                      </Grid>
                      <Grid item xs={2}>
                        <Button
                          variant="contained"
                          color="primary"
                          fullWidth
                          onClick={handleAddMetaField}
                          disabled={!newKey.trim() || !newValue.trim()}
                        >
                          <AddIcon />
                        </Button>
                      </Grid>
                    </Grid>
                  </Paper>
                )}
              </Box>
            </>
          ) : (
            /* Vista de detalles */
            <>
              <Typography variant="h4" gutterBottom>
                {board.name}
              </Typography>
              
              {description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Descripción
                  </Typography>
                  <Typography variant="body1" paragraph>
                    {description}
                  </Typography>
                </Box>
              )}
              
              <Divider sx={{ my: 2 }} />
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Tipo de tablero
                  </Typography>
                  <Typography variant="body1">
                    {getBoardTypeTitle()}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Estado
                  </Typography>
                  <Chip 
                    label={isPinned ? "Fijado" : "No fijado"} 
                    color={isPinned ? "primary" : "default"}
                    size="small"
                  />
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Creado el
                  </Typography>
                  <Typography variant="body2">
                    {formattedCreateDate}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Última actualización
                  </Typography>
                  <Typography variant="body2">
                    {formattedUpdateDate}
                  </Typography>
                </Grid>
              </Grid>
              
              {/* Sección de metadatos */}
              {metaDataFields.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" gutterBottom>
                    Información adicional
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {metaDataFields.map((field, index) => (
                      <Chip
                        key={index}
                        label={`${field.key}: ${field.value}`}
                        sx={{ m: 0.5 }}
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
        
        {/* Pie del modal con acciones */}
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          {isEditMode ? (
            <>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleSaveChanges}
                startIcon={<SaveIcon />}
                sx={{ mr: 1 }}
              >
                Guardar cambios
              </Button>
              <Button 
                variant="outlined" 
                onClick={() => setIsEditMode(false)}
              >
                Cancelar
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={() => setIsEditMode(true)}
                startIcon={<EditIcon />}
                sx={{ mr: 1 }}
              >
                Editar tablero
              </Button>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleOpenBoard}
                startIcon={<LaunchIcon />}
              >
                Abrir tablero
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default ViewBoardModal;